import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithPopup,
  unlink,
  onAuthStateChanged,
} from 'firebase/auth';
import type { User, UserCredential } from 'firebase/auth';
import { auth } from './config';

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Email/Password authentication
export async function signUpWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(result.user);
  return result;
}

export async function signInWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

// Social authentication
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error: unknown) {
    // Fall back to redirect on popup blocked
    if (error instanceof Error && error.message.includes('popup-blocked')) {
      await signInWithRedirect(auth, googleProvider);
      throw new Error('Redirecting to Google sign-in...');
    }
    throw error;
  }
}

export async function signInWithApple(): Promise<UserCredential> {
  try {
    return await signInWithPopup(auth, appleProvider);
  } catch (error: unknown) {
    // Fall back to redirect on popup blocked
    if (error instanceof Error && error.message.includes('popup-blocked')) {
      await signInWithRedirect(auth, appleProvider);
      throw new Error('Redirecting to Apple sign-in...');
    }
    throw error;
  }
}

// Handle redirect result (call on app load)
export async function handleRedirectResult(): Promise<UserCredential | null> {
  return getRedirectResult(auth);
}

// Sign out
export async function signOut(): Promise<void> {
  return firebaseSignOut(auth);
}

// Password management
export async function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No authenticated user');
  }

  // Re-authenticate before changing password
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

// Account linking
export async function linkGoogleAccount(): Promise<UserCredential> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  return linkWithPopup(user, googleProvider);
}

export async function linkAppleAccount(): Promise<UserCredential> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  return linkWithPopup(user, appleProvider);
}

export async function unlinkProvider(providerId: string): Promise<User> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  return unlink(user, providerId);
}

// Get linked providers
export function getLinkedProviders(): string[] {
  const user = auth.currentUser;
  if (!user) {
    return [];
  }
  return user.providerData.map((provider) => provider.providerId);
}

// Auth state listener
export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

// Get current user
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

// Send email verification
export async function sendVerificationEmail(): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  return sendEmailVerification(user);
}
